<template>
  <div
    class="rounded-xl -mx-16 -mt-64 -mb-24 px-16 py-24 md-1:-mx-24 md-1:px-24"
    :class="showSitterEmptyState ? 'bg-grey-background-contrast border-1 border-solid border-grey-divider-subdued' : ''"
  >
    <ratings-overview
      :count="totalReviewCount"
      :average="starAggregate"
      :distributionData="distributionData"
      :showSitterView="showSitterView"
      :isBasic="isBasic"
      class="mt-48"
    />
    <poppins-button
      v-if="showReviewCta"
      :value="buttonValue"
      variant="outlined"
      size="medium"
      class="profile-empty-state-btn w-full mt-16"
      :to="buttonTo"
      @click="handleReviewButtonClick"
    />
    <reviews-list
      :reviews="reviews"
      :totalReviewCount="totalReviewCount"
      :showReplies="true"
      :revieweeDisplayName="profileAccountData.displayName"
      :showOwnerView="editModeActive"
      :fullWidthBtn="true"
      class="mt-24"
      @view-more-reviews="$emit('view-more-reviews')"
    />
  </div>
</template>

<script lang="ts">
import RatingsOverview from '@/components/ratings_overview/RatingsOverview.vue';
import ReviewsList from '@/components/reviews_list/ReviewsList.vue';
import PoppinsButton from '@/components/button/Button.vue';
import { sendSegmentEvent } from '@/lib/segment';
import { VIEWER_CAN_REVIEW } from '@/graphql/provider/profile/queries';

interface Review {
  node: {
    reviewerShortName: string;
    stars: string;
    at: string;
    publicFeedback: string;
    response: string;
  };
}

interface DistributionData {
  count: number;
  percent: number;
  stars: number;
}

export default {
  name: 'ProfileReviews',
  components: {
    RatingsOverview,
    ReviewsList,
    PoppinsButton,
  },
  props: {
    reviewsData: {
      type: Object,
      default: () => ({}),
    },
    profileAccountData: {
      type: Object,
      default: () => ({}),
    },
    isProfileOwner: {
      type: Boolean,
      default: false,
    },
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
    previewModeActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showReviewCta: false,
    };
  },
  computed: {
    isBasic(): boolean {
      return this.accountTypeInfo?.accountType === 'basic';
    },
    reviews(): Array<Review> {
      return this.reviewsData?.edges || [];
    },
    totalReviewCount(): number {
      return this.reviewsData?.totalReviewCount || 0;
    },
    starAggregate(): number {
      return this.reviewsData?.starAggregate || 0;
    },
    distributionData(): Array<DistributionData> {
      return this.reviewsData?.ratingsDistribution || [];
    },
    showSitterView(): boolean {
      return this.isProfileOwner && !this.previewModeActive;
    },
    showSitterEmptyState(): boolean {
      return this.showSitterView && this.totalReviewCount === 0 && this.editModeActive;
    },
    buttonValue(): string {
      return this.showSitterView ? 'Request a review' : 'Leave a review';
    },
    buttonTo() {
      if (this.previewModeActive) {
        return null;
      }
      const returnPath = `${window.location.pathname}${window.location.search}`;
      if (this.showSitterView) {
        return {
          name: 'RequestTabs',
          query: { return_path: returnPath },
        };
      }
      return {
        name: 'newRating',
        params: { sitterId: this.profileAccountData.id },
        query: { return_path: returnPath },
      };
    },
  },
  mounted() {
    // If the profile does not belong to the viewer, check if they can leave a review
    if (this.isProfileOwner) {
      this.showReviewCta = true;
    } else {
      this.checkViewerCanReview();
    }
  },
  methods: {
    async checkViewerCanReview() {
      try {
        await this.$apollo
          .query({
            query: VIEWER_CAN_REVIEW,
            variables: {
              accountId: this.profileAccountData.id,
            },
          })
          .then(({ data }) => {
            this.showReviewCta = data.account?.canReview;
          });
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
      }
    },
    handleReviewButtonClick(event: Event): void {
      if (this.previewModeActive) {
        event.preventDefault();
        this.$emit('show-edit-mode-popup', 'PREVIEW_REVIEW');
        return;
      }

      // Only track the event if the profile doesn't belong to the viewer (i.e., when leaving a review)
      if (!this.isProfileOwner) {
        sendSegmentEvent('Leave Review for Sitter Clicked', {
          sitter_uuid: this.profileAccountData.id,
          sitter_display_name: this.profileAccountData.displayName,
          profile_url: window.location.pathname,
        });
      }
    },
  },
};
</script>
